import { useState } from 'react';
import { useProducts } from '@/hooks/useProducts';
import { useCategories } from '@/hooks/useCategories';
import { api } from '@/lib/api';
import type { CreateProductRequest, UpdateProductRequest, Product } from '@photobooth/types';

const defaultFormData = {
  name: '',
  categoryId: '',
  shotCount: 8,
  printCount: 1,
  paperType: 'collage_4x6' as 'collage_4x6' | 'strip',
  basePrice: 0,
  isPublic: true,
  productType: 'session' as 'session' | 'addon',
  countdownSeconds: '' as string | number,
  reviewPhotoSeconds: '' as string | number,
  editCountdownSeconds: '' as string | number,
};

export function ProductsPage() {
  const { products, isLoading, error, refetch } = useProducts();
  const { categories } = useCategories();
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const isEditMode = !!editingProduct;

  const openCreateForm = () => {
    setShowForm(true);
    setEditingProduct(null);
    setFormData(defaultFormData);
  };

  const openEditForm = (product: Product) => {
    setEditingProduct(product);
    setShowForm(false);
    setFormData({
      name: product.name,
      categoryId: product.categoryId,
      shotCount: product.shotCount,
      printCount: product.printCount,
      paperType: product.paperType as 'collage_4x6' | 'strip',
      basePrice: product.basePrice,
      isPublic: product.isPublic,
      productType: (product.productType || 'session') as 'session' | 'addon',
      countdownSeconds: product.countdownSeconds ?? '',
      reviewPhotoSeconds: product.reviewPhotoSeconds ?? '',
      editCountdownSeconds: product.editCountdownSeconds ?? '',
    });
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData(defaultFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const toNum = (v: string | number): number | undefined => {
        if (v === '' || v === null || v === undefined) return undefined;
        const n = typeof v === 'number' ? v : parseInt(String(v), 10);
        return isNaN(n) ? undefined : n;
      };

      const captureOverrides = {
        ...(toNum(formData.countdownSeconds) != null && { countdownSeconds: toNum(formData.countdownSeconds) }),
        ...(toNum(formData.reviewPhotoSeconds) != null && { reviewPhotoSeconds: toNum(formData.reviewPhotoSeconds) }),
        ...(toNum(formData.editCountdownSeconds) != null && { editCountdownSeconds: toNum(formData.editCountdownSeconds) }),
      };

      if (isEditMode && editingProduct) {
        const data: UpdateProductRequest = {
          name: formData.name,
          categoryId: formData.categoryId,
          shotCount: formData.shotCount,
          printCount: formData.printCount,
          paperType: formData.paperType,
          basePrice: formData.basePrice,
          isPublic: formData.isPublic,
          productType: formData.productType,
          ...captureOverrides,
        };
        const response = await api.products.update(editingProduct.productId, data);
        if (response.success) {
          closeForm();
          refetch();
        } else {
          alert(response.error?.message || 'Failed to update product');
        }
      } else {
        const data: CreateProductRequest = {
          name: formData.name,
          categoryId: formData.categoryId,
          shotCount: formData.shotCount,
          printCount: formData.printCount,
          paperType: formData.paperType,
          basePrice: formData.basePrice,
          isPublic: formData.isPublic,
          productType: formData.productType,
          ...captureOverrides,
        };
        const response = await api.products.create(data);
        if (response.success) {
          closeForm();
          refetch();
        } else {
          alert(response.error?.message || 'Failed to create product');
        }
      }
    } catch (err) {
      alert(isEditMode ? 'Failed to update product' : 'Failed to create product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (productId: string) => {
    if (!confirm('Are you sure you want to delete this product?')) return;
    try {
      await api.products.delete(productId);
      refetch();
    } catch (err) {
      alert('Failed to delete product');
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingProduct) return;
    setIsUploadingPhoto(true);
    try {
      const response = await api.products.uploadPhoto(editingProduct.productId, file);
      if (response.success && response.data) {
        setEditingProduct({ ...editingProduct, imageUrl: response.data!.url });
        refetch();
      } else {
        alert('Failed to upload image');
      }
    } catch (err) {
      alert('Failed to upload image');
    } finally {
      setIsUploadingPhoto(false);
      e.target.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text">Products</h1>
          <p className="text-text-muted mt-1">Manage photo booth products and pricing</p>
        </div>
        <button
          onClick={showForm || isEditMode ? closeForm : openCreateForm}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full md:w-auto"
        >
          {showForm || isEditMode ? 'Cancel' : '+ New Product'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {(showForm || isEditMode) && (
        <div className="bg-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-text mb-4">
            {isEditMode ? `Edit Product: ${editingProduct?.name}` : 'Create Product'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text mb-1">Product Type</label>
                <select
                  value={formData.productType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      productType: e.target.value as 'session' | 'addon',
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="session">Session Product</option>
                  <option value="addon">Add-on Product</option>
                </select>
                <p className="mt-1 text-xs text-text-muted">
                  Session products include a full photo session. Add-on products are extras like extra prints or digital copies.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Category *</label>
                <select
                  required
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.categoryId} value={cat.categoryId}>{cat.name}</option>
                  ))}
                </select>
              </div>
              {formData.productType === 'session' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">Shots *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.shotCount}
                      onChange={(e) =>
                        setFormData({ ...formData, shotCount: parseInt(e.target.value, 10) || 0 })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">Prints *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.printCount}
                      onChange={(e) =>
                        setFormData({ ...formData, printCount: parseInt(e.target.value, 10) || 0 })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">Paper Type *</label>
                    <select
                      value={formData.paperType}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          paperType: e.target.value as typeof formData.paperType,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="collage_4x6">4x6 Collage</option>
                      <option value="strip">Strip</option>
                    </select>
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-text mb-1">Base Price *</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.basePrice}
                  onChange={(e) => setFormData({ ...formData, basePrice: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              {formData.productType === 'session' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      Capture Countdown (sec)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      placeholder="0 = use global"
                      value={formData.countdownSeconds}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData({
                          ...formData,
                          countdownSeconds: v === '' ? '' : parseInt(v, 10) || 0,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      Review Photo (sec)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      placeholder="0 = use global"
                      value={formData.reviewPhotoSeconds}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData({
                          ...formData,
                          reviewPhotoSeconds: v === '' ? '' : parseInt(v, 10) || 0,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      Edit Countdown (sec)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="900"
                      placeholder="0 = use global"
                      value={formData.editCountdownSeconds}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData({
                          ...formData,
                          editCountdownSeconds: v === '' ? '' : parseInt(v, 10) || 0,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </>
              )}
            </div>
            <p className="text-xs text-text-muted -mt-2">
              Leave empty or 0 to use global config.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPublic"
                checked={formData.isPublic}
                onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
              />
              <label htmlFor="isPublic" className="text-sm font-medium text-text">
                Available on website
              </label>
            </div>
            {isEditMode && editingProduct && (
              <div>
                <label className="block text-sm font-medium text-text mb-1">Product Image</label>
                <p className="text-xs text-text-muted mb-2">Displayed on public website</p>
                <div className="aspect-video max-w-xs bg-gray-100 rounded-lg overflow-hidden mb-2 flex items-center justify-center">
                  {editingProduct.imageUrl ? (
                    <img
                      src={editingProduct.imageUrl}
                      alt={editingProduct.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-4xl text-gray-400">📷</span>
                  )}
                </div>
                <label className="block">
                  <span className="sr-only">Upload image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    disabled={isUploadingPhoto}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary file:text-white file:cursor-pointer hover:file:bg-primary-600"
                  />
                </label>
                {isUploadingPhoto && <p className="text-sm text-text-muted mt-1">Uploading...</p>}
              </div>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting
                ? (isEditMode ? 'Updating...' : 'Creating...')
                : (isEditMode ? 'Update Product' : 'Create Product')}
            </button>
          </form>
        </div>
      )}

      <div className="bg-surface rounded-lg shadow overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Shots</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Prints</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Price</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.map((product) => {
              const category = categories.find(c => c.categoryId === product.categoryId);
              return (
                <tr key={product.productId}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text">{product.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                    {product.productType === 'addon' ? 'Add-on' : 'Session'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">{category?.name || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                    {product.productType === 'addon' ? '-' : product.shotCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                    {product.productType === 'addon' ? '-' : product.printCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">Rp {product.basePrice.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => openEditForm(product)}
                      className="text-primary hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(product.productId)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
